import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import './Permissions.css';
import { useAuthStore } from '../store/authStore';
import * as permissionsApi from '../services/permissionsApi';
import { usersAPI, companiesAPI } from '../services/api';

function Permissions() {
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (user?.user_type !== 'gods_eye') {
      return;
    }
    const init = async () => {
      await Promise.all([loadManagers(), loadCompanies()]);
    };
    init();
  }, [user]);

  const loadManagers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      const usersData = response.data || response;
      const managerUsers = usersData.filter(u => u.user_type === 'manager');
      setManagers(managerUsers);
    } catch (error) {
      toast.error('Failed to load managers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.getPublic();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies', error);
    }
  };

  // Only gods_eye should manage manager/company assignments
  if (user?.user_type !== 'gods_eye') {
    return (
      <div className="container">
        <div className="alert alert-danger">
          <strong>Access Denied</strong><br />
          Only administrators can assign companies to managers.
        </div>
      </div>
    );
  }

  return (
    <div className="container permissions-container">
      <h1>Manager Assignments</h1>
      <p className="permissions-hint">
        Assign which companies each manager can see and manage. Managers will only see users and data for their assigned companies.
      </p>
      <div className="managers-list">
        {loading && <p className="loading">Loading managers...</p>}
        {managers.length === 0 && !loading && (
          <div className="empty-state">
            <p>No managers found</p>
          </div>
        )}
        {managers.map(manager => (
          <ManagerAssignmentCard 
            key={manager.id} 
            manager={manager} 
            companies={companies}
            isGodEye={user?.user_type === 'gods_eye'}
            onCompanyAssigned={loadManagers}
          />
        ))}
      </div>
    </div>
  );
}

// Manager Assignment Card Component
function ManagerAssignmentCard({ manager, companies = [], isGodEye = false, onCompanyAssigned }) {
  const [assignedCompanies, setAssignedCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedManager, setExpandedManager] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  useEffect(() => {
    if (expandedManager) {
      loadAssignedCompanies();
    }
  }, [expandedManager]);

  const loadAssignedCompanies = async () => {
    try {
      setLoading(true);
      const companyIds = await permissionsApi.getManagerCompanies(manager.id);
      setAssignedCompanies(companyIds);
    } catch (error) {
      toast.error('Failed to load assigned companies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!selectedCompanyId) {
      toast.error('Please select a company');
      return;
    }

    if (assignedCompanies.includes(parseInt(selectedCompanyId))) {
      toast.error('This company is already assigned');
      return;
    }

    try {
      await permissionsApi.assignCompanyToManager(manager.id, parseInt(selectedCompanyId));
      setAssignedCompanies(prev => [...prev, parseInt(selectedCompanyId)]);
      setSelectedCompanyId('');
      toast.success('Company assigned to manager');
      onCompanyAssigned?.();
    } catch (error) {
      const message = error?.response?.data?.detail || 'Failed to assign company to manager';
      toast.error(message);
    }
  };

  const handleRemoveCompany = async (companyId) => {
    try {
      await permissionsApi.removeCompanyFromManager(manager.id, companyId);
      setAssignedCompanies(prev => prev.filter(c => c !== companyId));
      toast.success('Company removed from manager');
    } catch (error) {
      const message = error?.response?.data?.detail || 'Failed to remove company from manager';
      toast.error(message);
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  return (
    <div className="manager-card">
      <button 
        className="manager-header"
        onClick={() => setExpandedManager(!expandedManager)}
      >
        <span className="manager-name">{manager.username}</span>
        <span className="manager-arrow">{expandedManager ? '▼' : '▶'}</span>
      </button>

      {expandedManager && (
        <div className="manager-details">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <div className="manager-grid">
              <div className="manager-column manager-column-username">
                <h5>Username</h5>
                <div className="manager-username-main">{manager.username}</div>
              </div>
              <div className="manager-column manager-column-companies">
                <h5>Companies ({assignedCompanies.length})</h5>
                {assignedCompanies.length === 0 ? (
                  <p className="text-muted">No companies assigned</p>
                ) : (
                  <div className="company-list">
                    {assignedCompanies.map(companyId => (
                      <div key={companyId} className="company-item">
                        <span>{getCompanyName(companyId)}</span>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveCompany(companyId)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {isGodEye && (
                  <div className="add-company-inline">
                    <div className="add-company-form">
                      <select 
                        value={selectedCompanyId}
                        onChange={(e) => setSelectedCompanyId(e.target.value)}
                        className="company-select"
                      >
                        <option value="">Select a company...</option>
                        {companies
                          .filter(c => !assignedCompanies.includes(c.id))
                          .map(c => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                      <button 
                        className="btn btn-sm btn-primary"
                        onClick={handleAddCompany}
                        disabled={!selectedCompanyId}
                      >
                        Assign
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Permissions;
